/**
 * Notification type → icon, accent and a plain-language label.
 *
 * Accents come from the design tokens in src/styles.css. Every accent is paired
 * with a text label ("Over cap", "Needs approval") so status is never carried by
 * color alone — the icon and the words say it too.
 *
 * KEEP IN SYNC WITH `NotificationType` in worker/lib/notify.ts — that union is
 * what the app raises, this map is what it looks like. `NotificationType` below
 * is a mirror of it, and TYPE_META is typed so that dropping one is a compile
 * error, not a silent generic bell. (The union should live in shared/types.ts;
 * that file is owned by the integrator, so it is duplicated here for now.)
 *
 * ALIASES: the columns comment in migration 0001 and some callers use older
 * names ('budget_warning', 'subscription_detected', 'subscription_price_increase').
 * They're mapped too, so a notification raised with either name still renders
 * with real copy. Anything genuinely unknown falls back to a neutral bell rather
 * than breaking the page.
 */
import type { ReactNode } from 'react';
import type { NotificationType } from '../../shared/types';



export interface TypeMeta {
  /** Short human label shown under the title. */
  label: string;
  /** Tailwind classes for the icon chip: soft background + accent ink. */
  chip: string;
  icon: ReactNode;
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" {...stroke} aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

const AlertTriangle = (
  <Svg>
    <path d="M10.3 3.9 2.4 17a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </Svg>
);

const Gauge = (
  <Svg>
    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
    <path d="m14.1 9.9 3.2-3.2" />
    <path d="M3.5 18a9 9 0 1 1 17 0" />
  </Svg>
);

const ClipboardCheck = (
  <Svg>
    <path d="M9 3h6a1 1 0 0 1 1 1v1H8V4a1 1 0 0 1 1-1Z" />
    <path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" />
    <path d="m9 13 2 2 4-4" />
  </Svg>
);

const BadgeCheck = (
  <Svg>
    <path d="M12 3.2 14 5l2.6-.3 1 2.4 2.4 1-.3 2.6 1.8 2-1.8 2 .3 2.6-2.4 1-1 2.4L14 19l-2 1.8-2-1.8-2.6.3-1-2.4-2.4-1 .3-2.6L2.5 11l1.8-2-.3-2.6 2.4-1 1-2.4L10 5l2-1.8Z" />
    <path d="m9 12 2 2 4-4" />
  </Svg>
);

const Repeat = (
  <Svg>
    <path d="m17 2 4 4-4 4" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <path d="m7 22-4-4 4-4" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </Svg>
);

const TrendingUp = (
  <Svg>
    <path d="m22 7-8.5 8.5-5-5L2 17" />
    <path d="M16 7h6v6" />
  </Svg>
);

const Utensils = (
  <Svg>
    <path d="M4 3v6a3 3 0 0 0 3 3 3 3 0 0 0 3-3V3" />
    <path d="M7 12v9" />
    <path d="M17 3c-1.7 1-2.5 3-2.5 5.5S15.3 13 17 14v7" />
  </Svg>
);

/** The board — a note pinned to the fridge. */
const Board = (
  <Svg>
    <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H9l-5 4V5.5Z" />
    <path d="M8 9h8M8 12h5" />
  </Svg>
);

const ClipboardPlus = (
  <Svg>
    <path d="M9 3h6a1 1 0 0 1 1 1v1H8V4a1 1 0 0 1 1-1Z" />
    <path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" />
    <path d="M12 10v6" />
    <path d="M9 13h6" />
  </Svg>
);

const Undo = (
  <Svg>
    <path d="M3 8h11a5 5 0 0 1 0 10H8" />
    <path d="m7 4-4 4 4 4" />
  </Svg>
);

const Wallet = (
  <Svg>
    <path d="M20 8V6a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6" />
    <path d="M17 13h.01" />
  </Svg>
);

const Bell = (
  <Svg>
    <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7" />
    <path d="M10.3 20a2 2 0 0 0 3.4 0" />
  </Svg>
);

/** A birthday cake. */
const Cake = (
  <Svg>
    <path d="M4 20h16M5 20v-7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7" />
    <path d="M8 11V8M12 11V7M16 11V8M12 4v1" />
  </Svg>
);

/** A goal reached. */
const Trophy = (
  <Svg>
    <path d="M8 4h8v5a4 4 0 0 1-8 0V4Z" />
    <path d="M8 6H5v1a3 3 0 0 0 3 3M16 6h3v1a3 3 0 0 1-3 3M10 15h4M9 20h6M12 15v5" />
  </Svg>
);

/** A map pin — someone reached a place. */
const MapPin = (
  <Svg>
    <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" />
    <path d="M12 10.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
  </Svg>
);

/** An arrow heading out — someone left a place. */
const ArrowOut = (
  <Svg>
    <path d="M5 12h13" />
    <path d="m13 6 6 6-6 6" />
  </Svg>
);

/** A battery running low. */
const BatteryLow = (
  <Svg>
    <path d="M4 8h13a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2Z" />
    <path d="M22 11v2" />
    <path d="M6 11v2" />
  </Svg>
);

const FALLBACK: TypeMeta = {
  label: 'Update',
  chip: 'bg-surface-sunk text-ink-soft',
  icon: Bell,
};

const OVER_CAP: TypeMeta = {
  label: 'Over cap',
  chip: 'bg-clay-soft text-clay',
  icon: AlertTriangle,
};
const NEAR_CAP: TypeMeta = {
  label: 'Close to cap',
  chip: 'bg-honey-soft text-honey',
  icon: Gauge,
};
const NEW_SUBSCRIPTION: TypeMeta = {
  label: 'New subscription',
  chip: 'bg-dusk-soft text-dusk',
  icon: Repeat,
};
const PRICE_INCREASE: TypeMeta = {
  label: 'Price increase',
  chip: 'bg-clay-soft text-clay',
  icon: TrendingUp,
};

/** Every type the app raises. Missing one here is a compile error, not a bell. */
const TYPE_META: Record<NotificationType, TypeMeta> & Record<string, TypeMeta> = {
  // money — adults only
  budget_over: OVER_CAP,
  budget_near: NEAR_CAP,
  subscription_new: NEW_SUBSCRIPTION,
  subscription_increase: PRICE_INCREASE,

  // chores + allowance
  chore_done: { label: 'Needs approval', chip: 'bg-dusk-soft text-dusk', icon: ClipboardCheck },
  chore_approved: { label: 'Approved', chip: 'bg-sage-soft text-sage', icon: BadgeCheck },
  chore_dismissed: { label: 'Sent back', chip: 'bg-honey-soft text-honey', icon: Undo },
  chore_assigned: { label: 'New chore', chip: 'bg-dusk-soft text-dusk', icon: ClipboardPlus },
  allowance_payout: { label: 'Paid out', chip: 'bg-sage-soft text-sage', icon: Wallet },

  // household
  meal_plan_empty: { label: 'Meal plan', chip: 'bg-sage-soft text-sage', icon: Utensils },
  message_posted: { label: 'The board', chip: 'bg-dusk-soft text-dusk', icon: Board },
  reminder_due: { label: 'Reminder', chip: 'bg-honey-soft text-honey', icon: Bell },
  birthday_soon: { label: 'Birthday', chip: 'bg-clay-soft text-clay', icon: Cake },
  goal_reached: { label: 'Goal reached', chip: 'bg-sage-soft text-sage', icon: Trophy },

  // location
  place_arrival: { label: 'Arrived', chip: 'bg-sage-soft text-sage', icon: MapPin },
  place_departure: { label: 'Left', chip: 'bg-dusk-soft text-dusk', icon: ArrowOut },
  battery_low: { label: 'Low battery', chip: 'bg-honey-soft text-honey', icon: BatteryLow },

  // aliases for the older type strings (migration 0001's column comment, and any
  // caller that reaches for notify() directly instead of a wrapper)
  budget_warning: NEAR_CAP,
  subscription_detected: NEW_SUBSCRIPTION,
  subscription_price_increase: PRICE_INCREASE,
};

export function metaFor(type: string): TypeMeta {
  return TYPE_META[type] ?? FALLBACK;
}

/** The reassuring icon on the empty state. */
export const CaughtUpIcon = (
  <svg viewBox="0 0 24 24" className="h-7 w-7" {...stroke} aria-hidden="true" focusable="false">
    <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" />
    <path d="m8.5 12.5 2.5 2.5 4.5-5" />
  </svg>
);
